/* eslint-disable @angular-eslint/template/label-has-associated-control */

import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { WidgetApiService } from 'libs/rx-form-builder/src/lib/rx-form-presenter/services/widget-api.service';

export interface MedicationData {
  date: string;
  medication: string;
  frequency: string;
  dosage: string;
  stopDate: string;
  widgetApiService?: WidgetApiService;
}

@Component({
  selector: 'app-add-medication-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgSelectModule,
  ],
  template: `
    <h3>Add Medication</h3>
    <div class="dialog-form">
      <div class="form-field">
        <label>Start Date</label>
        <input
          (focus)="startPicker.open()"
          [matDatepicker]="startPicker"
          [(ngModel)]="data.date"
          placeholder="MM/DD/YYYY"
        />
        <mat-datepicker #startPicker></mat-datepicker>
      </div>
      <div class="form-field">
        <label>Select Medication</label>
        <ng-select
          [(ngModel)]="data.medication"
          [items]="medications"
          bindLabel="label"
          bindValue="value"
          placeholder="Select Medication"
          [searchable]="true"
          [clearable]="true"
          [addTag]="addTagFn"
          addTagText="Add item"
          [loading]="loading"
          appendTo="body"
          class="ng-select-custom"
        >
          <ng-template ng-option-tmp let-item="item">
            <div class="option-item">
              <span class="option-label">{{ item.label }}</span>
              <span class="option-strength" *ngIf="item.strength">{{ item.strength }}</span>
            </div>
          </ng-template>
        </ng-select>
      </div>
      <div class="form-field">
        <label>Frequency</label>
        <input [(ngModel)]="data.frequency" placeholder="Frequency" />
      </div>
      <div class="form-field">
        <label>Dosage</label>
        <input [(ngModel)]="data.dosage" placeholder="Dosage" />
      </div>
      <div class="form-field">
        <label>Stop Date</label>
        <input
          (focus)="stopPicker.open()"
          [matDatepicker]="stopPicker"
          [(ngModel)]="data.stopDate"
          placeholder="MM/DD/YYYY"
        />
        <mat-datepicker #stopPicker></mat-datepicker>
      </div>
    </div>
    <div class="dialog-actions">
      <button (click)="onCancel()">Cancel</button>
      <button class="blue" (click)="onAdd()" [disabled]="!isValid()">Add</button>
    </div>
  `,
  styles: [
    `
      :host {
        h3 {
          padding: 20px 0 10px;
          margin: 0;
          font-size: 16.5px;
          font-weight: 400;
          color: #000;
        }

        .dialog-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
          max-width: 100%;
          padding: 15px 0;
        }

        .dialog-actions {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          gap: 10px;
          padding: 15px 0;

          button {
            font-size: 13.5px;
            font-weight: 400;
            background: transparent;
            color: #000;
            border: solid 1px #ccc;
            padding: 9px 15px;
            border-radius: 5px;
            outline: none;
            cursor: pointer;
            min-width: 75px;

            &.blue {
              background: #1962f3;
              border-color: #1962f3;
              color: white;
            }

            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          }
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-field label {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.6);
          font-weight: 400;
        }

        .form-field input {
          border-radius: 5px;
          border: solid 1px #ccc;
          padding: 8px;
        }

        .option-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }

        .option-label {
          flex: 1;
        }

        .option-strength {
          font-size: 12px;
          color: #666;
          margin-left: 8px;
        }
      }

      ::ng-deep {
        .ng-dropdown-panel.ng-select-custom {
          min-height: 56px;
          box-shadow: none;
          border-radius: 0 !important;
        }
      }
    `,
  ],
})
export class AddMedicationDialogComponent implements OnInit {
  medications = [];
  loading = true;

  constructor(
    public dialogRef: MatDialogRef<AddMedicationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MedicationData,
  ) {
    if (!data.date) {
      data.date = '';
    }
    if (!data.medication) {
      data.medication = '';
    }
    if (!data.frequency) {
      data.frequency = '';
    }
    if (!data.dosage) {
      data.dosage = '';
    }
    if (!data.stopDate) {
      data.stopDate = '';
    }
  }

  ngOnInit() {
    if (this.data.widgetApiService) {
      this.data.widgetApiService.getMedications().subscribe({
        next: (medications) => {
          this.medications = medications.map((medication: any) => ({
            value: medication.MedicationId || '',
            label: medication.MedicationName || '',
          }));
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading medications:', error);
          this.medications = [];
          this.loading = false;
        },
      });
    } else {
      this.loading = false;
    }
  }

  isValid(): boolean {
    return typeof this.data.medication === 'string'
      ? this.data.medication.trim().length > 0
      : !!this.data.medication;
  }

  addTagFn = (term: string) => {
    return term;
  };

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(): void {
    if (this.isValid()) {
      const selectedItem = this.medications.find((item) => item.value === this.data.medication);
      if (selectedItem) {
        this.data.medication = selectedItem.label;
      }
      this.dialogRef.close(this.data);
    }
  }
}
